"use client";

/**
 * Verdict banner at the top of the Compare result.
 *
 * Replaces the old row of repeating amber pills (when one listing
 * swept every metric, three chips all said "Elan 3 - Yeni Yasamal",
 * which read as noise). The deterministic winner now gets ONE hero
 * banner - photo, listing identity, price, deal % - and the metric
 * wins render as small chips: attached to the banner when the winner
 * swept them, or in a quiet row naming their own listing when the
 * wins are split.
 *
 * No LLM input here (see ./metrics.ts). Tied metrics are dropped
 * rather than faking a winner. If even the overall pick ties, the
 * banner self-hides and only the split chips render.
 */
import { useT, useLocale } from "@/lib/locale-context";
import { formatPrice } from "@/lib/api";
import { translateVerdict, type TranslationKey } from "@/lib/i18n";
import type { AnalysisResult } from "@/types";
import { colorForIndex } from "./palette";
import { computeHeadlineWinners } from "./metrics";
import { confidenceLevel } from "./confidence";
import Photo from "@/components/Photo";

interface Props {
  results: AnalysisResult[];
}

/** Numeric weight for a confidence level so we can tie-break "best
 *  overall" toward the more trustworthy pick. */
function confWeight(pct: number | null | undefined): number {
  const level = confidenceLevel(pct);
  return level === "high" ? 2 : level === "medium" ? 1 : 0;
}

/** "Best overall" with a confidence tie-break.
 *
 *  The deterministic winner from `computeHeadlineWinners` ranks purely
 *  on overpriced_pct (most underpriced wins). But a 2 % discount the
 *  model is highly confident in is a better pick than a 3 % discount it
 *  is barely sure of - the extra 1 % is inside the noise of a low-conf
 *  estimate. So when two listings are CLOSE on deal quality (within
 *  `CLOSE_PCT`), prefer the one the model is more confident about. We
 *  only override toward STRICTLY higher confidence, and only when the
 *  higher-confidence listing isn't meaningfully worse on price - never
 *  the other way round, so we can't downgrade a clearly-better deal.
 *
 *  Returns null on a genuine tie (mirrors the suppression rule in
 *  metrics.ts - a fake winner would read as a lie). */
const CLOSE_PCT = 2.0;
function bestOverallWithConfidence(
  results: AnalysisResult[],
  baseline: number | null,
): number | null {
  if (baseline == null) return baseline;
  const baseOp = results[baseline]?.overpriced_pct;
  if (typeof baseOp !== "number") return baseline;

  let bestIdx = baseline;
  let bestConf = confWeight(results[baseline]?.confidence_pct);
  let bestOp = baseOp;

  results.forEach((r, i) => {
    if (i === bestIdx) return;
    const op = r.overpriced_pct;
    if (typeof op !== "number") return;
    const conf = confWeight(r.confidence_pct);
    if (conf > bestConf && op <= bestOp + CLOSE_PCT) {
      bestIdx = i;
      bestConf = conf;
      bestOp = op;
    }
  });

  return bestIdx;
}

interface MetricSpec {
  labelKey: TranslationKey;
  index: number | null;
}

export default function HeadlineChips({ results }: Props) {
  const t = useT();
  const { locale } = useLocale();
  if (results.length === 0) return null;

  const winners = computeHeadlineWinners(results);
  const bestOverall = bestOverallWithConfidence(results, winners.bestOverall);

  const metrics: MetricSpec[] = (
    [
      { labelKey: "compare.headline.cheapest", index: winners.cheapest },
      { labelKey: "compare.headline.best_ppm", index: winners.bestPpm },
      { labelKey: "compare.headline.closest_metro", index: winners.closestMetro },
      { labelKey: "compare.headline.fewest_flags", index: winners.fewestFlags },
    ] satisfies MetricSpec[]
  ).filter((m) => m.index !== null);

  // Metric wins the banner absorbs vs the ones that belong to OTHER
  // listings (rendered as their own row below).
  const sweptByWinner = metrics.filter((m) => m.index === bestOverall);
  const splitWins = metrics.filter((m) => m.index !== bestOverall);

  if (bestOverall == null && splitWins.length === 0) return null;

  const winner = bestOverall != null ? results[bestOverall] : null;
  const winnerColor = bestOverall != null ? colorForIndex(bestOverall) : null;
  const op = winner?.overpriced_pct;
  const dealLine =
    typeof op !== "number"
      ? null
      : op < -0.5
        ? t("compare.headline.under_market").replace("{pct}", Math.abs(op).toFixed(1))
        : op > 0.5
          ? t("compare.headline.over_market").replace("{pct}", op.toFixed(1))
          : t("compare.headline.at_market");
  const priceUnit =
    winner?.analysis_mode === "rent" ? t("result.permonth") : "AZN";

  const priceUnitLabel = priceUnit;
  const winnerPhoto = winner?.photo_urls?.[0] ?? null;
  const reasons = [
    dealLine,
    ...sweptByWinner.map(({ labelKey }) => t(labelKey).toLowerCase()),
  ].filter((x): x is string => Boolean(x));
  // Split wins grouped by the listing that owns them: one quiet row per
  // listing, so the runner-up's edge reads as a sentence, not confetti.
  const leadsBy = new Map<number, string[]>();
  for (const m of splitWins) {
    const i = m.index as number;
    leadsBy.set(i, [...(leadsBy.get(i) ?? []), t(m.labelKey).toLowerCase()]);
  }
  const dealTone =
    typeof op === "number" && op < -0.5
      ? "text-emerald-700 dark:text-emerald-300"
      : typeof op === "number" && op > 0.5
        ? "text-amber-700 dark:text-amber-400"
        : "text-gray-700";

  return (
    <div className="space-y-2">
      {/* The pick, with its evidence on the same card: identity, price,
          the reasons in one sentence, and the rule that produced it. */}
      {winner && winnerColor && (
        <div className="rounded-2xl border border-gray-200 bg-white p-4 sm:p-5">
          <div className="flex items-start gap-4">
            {winnerPhoto && (
              // eslint-disable-next-line @next/next/no-img-element
              <Photo src={winnerPhoto} alt="" frame="hidden h-16 w-24 shrink-0 rounded-lg sm:block" />
            )}
            <div className="min-w-0 flex-1">
              <div className="text-xs font-semibold text-gray-500">
                {t("compare.headline.best_overall")}
              </div>
              <div className="mt-1 flex flex-wrap items-baseline gap-x-3 gap-y-1">
                <span className="inline-flex items-baseline gap-1.5 text-lg font-extrabold text-gray-900">
                  <span className={`h-2.5 w-2.5 self-center rounded-full ${winnerColor.dot}`} aria-hidden />
                  {t("compare.cards.listing_label").replace("{n}", String((bestOverall ?? 0) + 1))}
                  {winner.location_name ? (
                    <span className="font-semibold text-gray-500">· {winner.location_name}</span>
                  ) : null}
                </span>
                <span className="text-lg font-extrabold tabular-nums text-gray-900">
                  {formatPrice(winner.price)}{" "}
                  <span className="text-xs font-medium text-gray-500">{priceUnitLabel}</span>
                </span>
                <span className="text-xs text-gray-500">{translateVerdict(winner.verdict, locale)}</span>
              </div>
              {reasons.length > 0 && (
                <p className={`mt-1.5 text-sm font-medium ${dealTone}`}>
                  {reasons.join(" · ")}
                </p>
              )}
              <p className="mt-2 text-[11px] leading-snug text-gray-400">
                {t("compare.headline.rule")}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Where the other listings lead - one line each. */}
      {leadsBy.size > 0 && (
        <div className="flex flex-col gap-1.5 px-1 text-xs text-gray-600">
          {[...leadsBy.entries()].map(([i, wins]) => {
            const c = colorForIndex(i);
            const r = results[i];
            if (!r) return null;
            const label = `${t("compare.cards.listing_label").replace("{n}", String(i + 1))}${r.location_name ? ` · ${r.location_name}` : ""}`;
            return (
              <div key={i} className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
                <span className={`h-1.5 w-1.5 rounded-full ${c.dot}`} aria-hidden />
                <span className="font-semibold text-gray-800">
                  {t("compare.headline.leads").replace("{listing}", label)}:
                </span>
                <span>{wins.join(", ")}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
